/**
 * After a write succeeds, what is on the screen is read again.
 *
 * THE BUG THIS EXISTS FOR (New Bug Auto Refresh). A person created a work
 * order, saw "Work order created", and the list did not show it until they
 * refreshed the browser. Every screen was responsible for remembering, at
 * every one of several hundred write sites, which queries that write changed
 * — and a site that forgot, or named the wrong key, or changed something that
 * ANOTHER panel on the same screen also shows (a count, a KPI, a detail
 * drawer), left the screen showing the old figures with nothing to say so.
 * A stale list looks exactly like a fresh one.
 *
 * SO IT IS NOT LEFT TO THE SCREEN. Every write in this app goes through one of
 * three senders — the axios instance in utils/api.ts (apiPost, apiPut,
 * apiPatch, apiDelete, the *Envelope and upload variants), the offline queue
 * in services/pendingQueue.ts (captureAndSend and its replays), and the
 * portal fetch in utils/portalSession.ts — and each of them calls
 * `notifyWrite` when the server confirms a write. That marks every cached
 * query stale and re-reads the ones a mounted screen is showing. A page does
 * nothing to get this; a page cannot forget it.
 *
 * The few reads that live in component state rather than in React Query are
 * not reached by invalidation; they subscribe with `useRefreshOnWrite` (see
 * hooks/useRefreshOnWrite.ts). The guard in
 * src/__tests__/everyWriteRefreshes.test.ts fails when a new one does not.
 *
 * Explicit `invalidateQueries` calls already in the pages are kept. They are
 * harmless, and a fetch one of them starts after the write is not started
 * again here (see connectQueryClient).
 */
import type { Query, QueryClient } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Requests that change nothing, and so must not cause a re-read.
// ---------------------------------------------------------------------------

/**
 * POSTs that only ASK. Each entry says why it is safe to skip. A write that
 * is wrongly listed here would bring the original bug back for that one
 * screen, so the list is short, literal, and checked by test.
 */
export const NO_REFRESH: ReadonlyArray<{ pattern: RegExp; reason: string }> = [
  {
    pattern: /^\/auth\//,
    reason: 'Sign-in, sign-out, token refresh and password reset change the session, not '
      + 'any figure on a screen. Re-reading after sign-out would also send every query '
      + 'with no token and bounce the person to /login a second time.',
  },
  {
    pattern: /\/preview(\?|$)/,
    reason: 'A preview renders what WOULD happen (bulk desk booking lines, a notification '
      + 'or report template, a badge) and stores nothing.',
  },
  {
    pattern: /[?&]dry_run=true\b/,
    reason: 'A dry run reports what a run would remove without removing it.',
  },
  {
    pattern: /^\/analytics\/reports\/custom(\?|$)/,
    reason: 'Runs a custom report and returns its rows; nothing is saved.',
  },
  {
    pattern: /^\/analytics\/occupancy-forecast(\?|$)/,
    reason: 'Computes a forecast from existing readings; nothing is saved.',
  },
  {
    pattern: /^\/leases\/scenarios\/calculate(\?|$)/,
    reason: 'The lease scenario calculator: it reads the lease and returns figures, and '
      + 'saves nothing (routers/lease.py calculate_scenario only SELECTs).',
  },
  {
    pattern: /^\/tenants\/contact-check(\?|$)/,
    reason: 'Asks whether a contact already exists while the form is being typed in.',
  },
  {
    pattern: /^\/admin\/sftp\/test(\?|$)/,
    reason: 'Tests a connection with the details on the form; nothing is saved.',
  },
];

/** True when this request changes nothing a screen shows. `url` is the path
 *  after `/api` (what axios is given), with or without a query string. */
export function isRefreshExempt(url: string | undefined): boolean {
  if (!url) return false;
  // An absolute URL, or one that still carries the /api prefix (raw fetch
  // callers build those), is reduced to the same path axios would see.
  const path = url.replace(/^https?:\/\/[^/]+/, '').replace(/^\/api(?=\/)/, '');
  return NO_REFRESH.some(({ pattern }) => pattern.test(path));
}

// ---------------------------------------------------------------------------
// The signal.
// ---------------------------------------------------------------------------

type Listener = () => void;
/** Called when the re-read is due. */
const listeners = new Set<Listener>();
/** Called synchronously the moment a write is confirmed, before any page code runs. */
const confirmListeners = new Set<Listener>();

/**
 * Writes close together are answered with ONE re-read, not one each.
 *
 * The first write is answered on the next tick — after the page's own
 * onSuccess has run, so a fetch it started is seen and not repeated. Writes
 * that arrive within this gap of the last re-read are gathered into a single
 * trailing one: a bulk loop of fifty assignments re-reads the screen a few
 * times rather than fifty, and the LAST re-read always comes after the LAST
 * write, which is the only one whose answer is shown.
 */
export const MIN_REFRESH_GAP_MS = 300;

let timer: ReturnType<typeof setTimeout> | null = null;
let lastFlushAt = -Infinity;

function each(set: Set<Listener>): void {
  for (const fn of [...set]) {
    try {
      fn();
    } catch {
      // One subscriber failing must not stop the rest from refreshing.
    }
  }
}

function flush(): void {
  timer = null;
  lastFlushAt = Date.now();
  each(listeners);
}

/**
 * Tell the app a write succeeded.
 *
 * Called by the senders, never by pages — a page that has a reason to call it
 * has found a sender that is missing from the list in this file's header.
 * Requests in NO_REFRESH, and calls made with `skipRefresh`, are ignored.
 */
export function notifyWrite(url?: string, opts: { skipRefresh?: boolean } = {}): void {
  if (opts.skipRefresh || isRefreshExempt(url)) return;
  each(confirmListeners);
  schedule();
  tellOtherTabs();
}

function schedule(): void {
  if (timer !== null) return;
  const wait = Math.max(0, lastFlushAt + MIN_REFRESH_GAP_MS - Date.now());
  timer = setTimeout(flush, wait);
}

// ---------------------------------------------------------------------------
// The other tabs.
//
// 🔴 A WRITE IN ONE TAB LEFT THE OTHER TABS STALE (Employee fix, final round —
// bug 5 checked as a tester would). With My visitors › Upcoming open in one
// tab, a visitor invited from a second tab never appeared in the first; a
// repeating desk booking made, then cancelled, in one tab never reached My
// bookings › Upcoming open in the other. The signal above reached only the
// tab that wrote, and refetchOnWindowFocus is off app-wide (main.tsx), so
// even coming back to the tab re-read nothing.
//
// The same signal now goes to every other tab of the app in this browser
// (BroadcastChannel: same origin, so the same sign-in), and each runs the
// same re-read it would for a write of its own. Only "something changed" is
// sent — never a URL, a body or an answer. A message received is not sent on
// again, so two tabs cannot echo each other. Where BroadcastChannel does not
// exist (old browsers, the test DOM) this is a no-op and nothing else changes.
// ---------------------------------------------------------------------------

export const DATA_CHANGED_CHANNEL = 'spaciora-data-changed';

let channel: BroadcastChannel | null | undefined;

function theChannel(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  try {
    channel = typeof BroadcastChannel === 'function' ? new BroadcastChannel(DATA_CHANGED_CHANNEL) : null;
  } catch {
    channel = null;
  }
  if (channel) {
    channel.onmessage = () => {
      // Another tab's write: re-read here exactly as for one of ours.
      each(confirmListeners);
      schedule();
    };
  }
  return channel;
}

function tellOtherTabs(): void {
  try {
    theChannel()?.postMessage('changed');
  } catch {
    // A closed channel must never fail the write that succeeded.
  }
}

/**
 * Tell ONLY the other tabs that something changed. For a write sent with
 * `skipRefresh` because its own tab cannot take the usual re-read: a job the
 * technician just declined or moved away is no longer theirs, so re-reading
 * the page still on screen answers 404 (FM Technician R2 e2e F1). That tab
 * refreshes its lists itself (components/Tech/job/leaveJob); the others re-read
 * as for any write.
 */
export function notifyOtherTabs(): void {
  tellOtherTabs();
}

/** Listen for other tabs' writes from app start, before this tab writes
 *  anything (connectQueryClient calls it). */
export function listenToOtherTabs(): void {
  theChannel();
}

/** Subscribe to "data changed". Returns the unsubscribe. */
export function onDataChanged(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * Wire a QueryClient to the signal: on every write, invalidate EVERYTHING.
 *
 * Everything, not a list of keys, because a list of keys is exactly what the
 * pages were maintaining and getting wrong. `invalidateQueries()` re-reads
 * only the queries something on screen is using; the rest are marked stale
 * and read when next shown, which costs nothing now.
 *
 * A query that started fetching AFTER the write was confirmed — the page's
 * own invalidateQueries in its onSuccess does exactly that — is left to
 * finish rather than cancelled and sent again. One that was already in flight
 * when the write landed is restarted, because its answer may predate it.
 */
export function connectQueryClient(client: QueryClient): () => void {
  let inFlightAtWrite = new Set<Query>();
  const onConfirm: Listener = () => {
    for (const q of client.getQueryCache().findAll({ fetchStatus: 'fetching' })) {
      inFlightAtWrite.add(q);
    }
  };
  confirmListeners.add(onConfirm);
  listenToOtherTabs();
  const offSignal = onDataChanged(() => {
    const predatesWrite = inFlightAtWrite;
    inFlightAtWrite = new Set();
    void client.invalidateQueries({
      predicate: (q) => q.state.fetchStatus !== 'fetching' || predatesWrite.has(q),
    });
  });
  return () => {
    confirmListeners.delete(onConfirm);
    offSignal();
  };
}

/** Test seam: forget the pending timer and every subscriber. */
export function __resetRefreshAfterWriteForTests(): void {
  if (timer !== null) clearTimeout(timer);
  timer = null;
  lastFlushAt = -Infinity;
  listeners.clear();
  confirmListeners.clear();
  try { channel?.close(); } catch { /* already closed */ }
  channel = undefined;
}
